/// <reference types="vite/client" />

/** Every VITE_-prefixed env var the app reads. Merged into ImportMetaEnv
 *  below, and the exact type of the filtered `viteEnv` object — non-VITE keys
 *  (MODE, DEV, …) exist on import.meta.env but not on the default export. */
interface ViteEnvVars {
  readonly VITE_API_BASE_URL?: string
  readonly VITE_DATADOG_APP_ID?: string
  readonly VITE_DATADOG_CLIENT_TOKEN?: string
  readonly VITE_DATADOG_PROXY?: string
  readonly VITE_DEFAULT_COUNTRY_CODE?: string
  readonly VITE_DEFAULT_TIMEZONE?: string
  readonly VITE_FEATURE_FLAGS_MODE?: string
  readonly VITE_DIGA_ENABLED?: string
  readonly VITE_DISEASE?: string
  readonly VITE_GEO?: string
  readonly VITE_KAIA_APP_DEEP_LINK?: string
  readonly VITE_LINK_APP_STORE_DE?: string
  readonly VITE_LINK_APP_STORE_DEFAULT?: string
  readonly VITE_LINK_APP_STORE_ES?: string
  readonly VITE_LINK_GOOGLE_PLAY_DE?: string
  readonly VITE_LINK_GOOGLE_PLAY_DEFAULT?: string
  readonly VITE_LINK_GOOGLE_PLAY_ES?: string
  readonly VITE_MODE?: string
  readonly VITE_PUBLIC_PATH?: string
  readonly VITE_SNOWPLOW_APP_ID?: string
  readonly VITE_SNOWPLOW_COLLECTOR?: string
  readonly VITE_SUPPORTED_LANG_CODES?: string
  readonly VITE_SUPPORT_EMAIL?: string
  readonly VITE_VERSION?: string
  readonly VITE_MATOMO_URL?: string
}

declare global {
  /** Vite opt-in: removes ImportMetaEnv's Record<string, any> fallback — only
   *  declared keys exist on import.meta.env, so new VITE_ vars must be added
   *  to ViteEnvVars above before use. */
  interface ViteTypeOptions {
    strictImportMetaEnv: unknown
  }

  interface ImportMetaEnv extends ViteEnvVars {}

  interface ImportMeta {
    readonly env: ImportMetaEnv
  }
}

// Object.entries(import.meta.env) is used intentionally — direct property
// accesses (import.meta.env.VITE_FOO or import.meta.env[k]) are statically
// inlined by Vite at transform time, which shadows runtime values injected by
// Vitest's test.env. Passing import.meta.env as an argument to Object.entries
// keeps it as a live object reference that includes all runtime values.
const viteEnv = Object.fromEntries(
  Object.entries(import.meta.env).filter(([k]) => k.startsWith('VITE_')),
) as ViteEnvVars

const isCopd = viteEnv.VITE_DISEASE === 'copd'
const isMsk = viteEnv.VITE_DISEASE === 'msk'
const isUs = viteEnv.VITE_GEO === 'us'
const isEu = viteEnv.VITE_GEO === 'eu'

export default {
  ...viteEnv,
  isProd: viteEnv.VITE_MODE === 'prod',
  isCopd,
  isMsk,
  isUs,
  isEu,
  isMskEu: isMsk && isEu,
  isMskUs: isMsk && isUs,
  isCopdEu: isCopd && isEu,
  isCopdUs: isCopd && isUs,
  features: {
    diga: viteEnv.VITE_DIGA_ENABLED === 'true',
  },
}
